import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';

import {
  Button, Input, Select, Textarea,
} from '@src/components/ui';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import {
  useCreateSellerFaqMutation,
  useDeleteSellerFaqMutation,
  useQuery,
  useReorderSellerFaqMutation,
  useUpdateSellerFaqMutation,
} from '@src/query';

import PageHeader from './PageHeader';


/** Suggested starting questions. These are PROMPTS ONLY — nothing is written to the
 * storefront until the seller types their own answer, so we never publish a claim
 * (warranty terms, return windows) that the seller did not make themselves. */
const STARTERS = [
  'Do you offer a warranty?',
  'What is your return policy?',
  'How soon do you ship?',
  'Can I pick this up in person?',
  'Is there a core charge?',
];


function Editor({
  initial, partTypes, onSave, onCancel, saving,
}) {
  const [question, setQuestion] = useState(initial?.question || '');
  const [answer, setAnswer] = useState(initial?.answer || '');
  const [partType, setPartType] = useState(initial?.part_type || '');

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-5">
      <Input
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder="Question (e.g. Do you offer a warranty?)"
      />

      <Textarea
        value={answer}
        rows={4}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder="Answer — buyers see this exactly as written."
      />

      {question
        ? null
        : (
          <div className="flex flex-wrap gap-1.5">
            {STARTERS.map((starter) => (
              <Button
                key={starter}
                variant="secondary"
                className="h-auto rounded-full px-2.5 py-1 text-xs"
                onClick={() => setQuestion(starter)}
              >
                {starter}
              </Button>
            ))}
          </div>
        )}

      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-subtle">Show on</span>

        <Select value={partType} onChange={(e) => setPartType(e.target.value)}>
          <option value="">Every part</option>

          {partTypes.map((type) => (
            <option key={type.id} value={type.id}>{`${type.name} only`}</option>
          ))}
        </Select>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>

        <Button
          disabled={!question.trim() || !answer.trim() || saving}
          loading={saving}
          onClick={() => onSave({
            question: question.trim(),
            answer: answer.trim(),
            partType: partType || null,
          })}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

Editor.displayName = 'Editor';


/** Storefront FAQ (#282): the buyer-facing FAQ block rendered on every public part page.
 * READ = Owner/Manager/Lister (read-only list, no write controls); WRITE/MANAGE
 * (add/edit/delete/reorder) = Owner/Manager. Viewers / non-members are redirected out.
 *
 * DELIBERATELY SELLER-LEVEL, NOT PER-PART — a yard with thousands of parts will never
 * author a distinct FAQ per row, so a per-part editor would ship empty and deflect
 * nothing. "Show on" is an OPTIONAL narrowing to one part type; the default is every
 * part. The API enforces the same read/write matrix; this gating is just UX.
 */
function StorefrontFaq() {
  const { seller, membership } = useActiveOrg();
  const { canManageOrg, canWrite } = usePermissions();

  const { data = [], isSuccess } = useQuery(`/sellers/faqs/${seller?.id}/`, { enabled: Boolean(seller) });
  const partTypesQuery = useQuery('/catalog/parts/types/');
  const create = useCreateSellerFaqMutation();
  const update = useUpdateSellerFaqMutation();
  const remove = useDeleteSellerFaqMutation();
  const reorder = useReorderSellerFaqMutation();

  const [editing, setEditing] = useState(null); // 'new' | id | null

  if (membership && !canWrite) {
    return <Navigate to="/app/settings" />;
  }

  const sellerId = seller?.id;
  // A non-manager (Lister) can never enter edit mode — the write affordances that set
  // `editing` are gated below, but treat it as null defensively so the read-only list
  // never flips into an Editor branch.
  const activeEdit = canManageOrg ? editing : null;
  const partTypes = partTypesQuery.data?.results || [];
  const move = (index, delta) => {
    const ids = data.map((faq) => faq.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) {
      return;
    }
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate({ sellerId, orderedIds: ids });
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader
        title="Storefront FAQ"
        subtitle="Answers shown on every public part page — the questions buyers ask before they message you."
      />

      <div className="flex w-full max-w-[640px] flex-col gap-3 px-4 pb-6 lg:px-0">
        {canManageOrg
          ? (activeEdit === 'new'
            ? (
              <Editor
                partTypes={partTypes}
                saving={create.isPending}
                onCancel={() => setEditing(null)}
                onSave={(vals) => create.mutate({ sellerId, ...vals }, { onSuccess: () => setEditing(null) })}
              />
            )
            : (
              <Button className="self-start" onClick={() => setEditing('new')}>
                <Plus className="size-4" />
                {' '}
                Add question
              </Button>
            ))
          : null}

        {isSuccess && data.length === 0 && activeEdit !== 'new'
          ? (
            <p className="text-sm text-ink-muted">
              {canManageOrg
                ? 'No FAQ yet — the block stays hidden on your storefront until you add one.'
                : 'No FAQ has been added yet.'}
            </p>
          )
          : null}

        {data.map((faq, index) => (activeEdit === faq.id
          ? (
            <Editor
              key={faq.id}
              initial={faq}
              partTypes={partTypes}
              saving={update.isPending}
              onCancel={() => setEditing(null)}
              onSave={(vals) => update.mutate({ sellerId, id: faq.id, ...vals }, { onSuccess: () => setEditing(null) })}
            />
          )
          : (
            <div key={faq.id} className="flex items-start gap-3 rounded-lg border border-hairline bg-surface-1 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{faq.question}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-table text-ink-muted">{faq.answer}</p>

                <p className="mt-1.5 text-xs text-ink-subtle">
                  {faq.part_type_name ? `Shown on ${faq.part_type_name} parts only` : 'Shown on every part'}
                </p>
              </div>

              {canManageOrg
                ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      aria-label="Move up"
                      className="size-8 p-0"
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ChevronUp className="size-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      aria-label="Move down"
                      className="size-8 p-0"
                      disabled={index === data.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ChevronDown className="size-4" />
                    </Button>

                    <Button variant="ghost" aria-label="Edit" className="size-8 p-0" onClick={() => setEditing(faq.id)}>
                      <Pencil className="size-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      aria-label="Delete"
                      className="size-8 p-0 text-danger"
                      onClick={() => remove.mutate({ sellerId, id: faq.id })}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )
                : null}
            </div>
          )))}
      </div>
    </div>
  );
}

StorefrontFaq.displayName = 'StorefrontFaq';
export default StorefrontFaq;
